import {Injectable, signal} from '@angular/core';
import {lastValueFrom, startWith} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Contact, ContactCountsResponse, ContactFilterParams, ContactFiltersResponse, ContactPaginatedResponse} from '../../models/contact';
import {environment} from '../../environments/environment';
import {RealtimeService} from './realtime.service';
import {PaginationInfo} from '../../models/la';

export type ViaCepResponse = {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
};

export class Address {
  zipCode: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  number: string;
  complement?: string;

  constructor(
    zipCode: string,
    street: string,
    neighborhood: string,
    city: string,
    state: string,
    number: string,
    complement?: string
  ) {
    this.zipCode = zipCode;
    this.street = street;
    this.neighborhood = neighborhood;
    this.city = city;
    this.state = state;
    this.number = number;
    this.complement = complement;
  }

  static fromJson(json?: { [key: string]: any }): Address {
    if (json == null) {
      return Address.empty();
    }
    return new Address(
      json['zipCode'],
      json['street'],
      json['neighborhood'],
      json['city'],
      json['state'],
      json['number'],
      json['complement']
    );
  }

  static empty(): Address {
    return new Address('', '', '', '', '', '', '');
  }

  toJson(): { [key: string]: any } {
    return {
      zipCode: this.zipCode,
      street: this.street,
      neighborhood: this.neighborhood,
      city: this.city,
      state: this.state,
      number: this.number,
      complement: this.complement
    };
  }

  static fromViaCepResponse(data: ViaCepResponse): Address {
    return new Address(
      data.cep,
      data.logradouro,
      data.bairro,
      data.localidade,
      data.uf,
      '0',
      ''
    );
  }
}

@Injectable({
  providedIn: 'root'
})
export class ContactService {

  contactList = signal<Contact[]>([])
  contactPagination = signal<PaginationInfo | null>(null)
  contactFilters = signal<ContactFiltersResponse | null>(null)
  contactCounts = signal<ContactCountsResponse | null>(null)
  loading = signal(false);

  private currentParams: ContactFilterParams = { limit: 10, offset: 0 };
  private currentBirthdayStartDate: number | null = null;
  private currentBirthdayEndDate: number | null = null;

  constructor(
    private realtime: RealtimeService,
    private http: HttpClient
  ) {
    this.realtime.contacts.pipe(startWith(null))
      .subscribe(()=>{
        this.refreshCurrentPage();
        if (this.currentBirthdayStartDate !== null && this.currentBirthdayEndDate !== null) {
          this.getContactBirthdays(this.currentBirthdayStartDate, this.currentBirthdayEndDate);
        }
      })
  }

  async refreshCurrentPage() {
    await this.getContactsPaginated(this.currentParams);
  }

  async getContactsPaginated(params: ContactFilterParams = {}): Promise<ContactPaginatedResponse> {
    this.loading.set(true);
    this.currentParams = {
      limit: params.limit ?? this.currentParams.limit,
      offset: params.offset ?? this.currentParams.offset,
      search: params.search || undefined,
      users: params.users?.length ? params.users : undefined,
      type: params.type || undefined,
    };
    const queryParams = new URLSearchParams();
    if (this.currentParams.limit !== undefined) queryParams.set('limit', this.currentParams.limit.toString());
    if (this.currentParams.offset !== undefined) queryParams.set('offset', this.currentParams.offset.toString());
    if (this.currentParams.search) queryParams.set('search', this.currentParams.search);
    if (this.currentParams.users && this.currentParams.users.length > 0) queryParams.set('users', this.currentParams.users.join(','));
    if (this.currentParams.type) queryParams.set('type', this.currentParams.type);
    const queryString = queryParams.toString();
    const url = `${environment.backendUrl}/v2/contacts${queryString ? `?${queryString}` : ''}`;
    const result = await lastValueFrom(this.http.get<ContactPaginatedResponse>(url));
    this.contactList.set(result.data);
    this.contactPagination.set(result.pagination);
    this.contactFilters.set(result.filters);
    this.contactCounts.set(result.counts);
    this.loading.set(false);
    return result;
  }

  async getContactById(contactId: string): Promise<Contact> {
    return await lastValueFrom(this.http.get<Contact>(`${environment.backendUrl}/contacts/${contactId}`));
  }

  async searchContacts(query: string = '', limit: number = 20): Promise<Contact[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', limit.toString());
    return await lastValueFrom(this.http.get<Contact[]>(
      `${environment.backendUrl}/v2/contacts/search?${params.toString()}`
    ));
  }

  contactBirthdays = signal<Contact[]>([]);

  async getContactBirthdays(startDate: number, endDate: number): Promise<Contact[]> {
    this.currentBirthdayStartDate = startDate;
    this.currentBirthdayEndDate = endDate;
    const result = await lastValueFrom(
      this.http.get<{ data: Contact[], pagination: { total: number } }>(
        `${environment.backendUrl}/v2/contacts/birthdays?startDate=${startDate}&endDate=${endDate}`
      )
    );
    this.contactBirthdays.set(result.data);
    return result.data;
  }

  async updateContactAssignee(id: string, assigneeUserId: string) {
    await lastValueFrom(this.http.put<Contact>(`${environment.backendUrl}/contacts/${id}/assigneeUserId`, {assigneeUserId}));
    await this.refreshCurrentPage();
  }

  async createContact(name: string, type: string, address?: string, email?: string,
                      phone?: string, birthday?: number, gender?: string, metadata?: string,
                      assigneeUserId?: string, pictureId?: string, cnpj?: string, lineOfWork?: string,
                      addressNumber?: string, addressComp?: string, city?: string, site?: string, profession?: string, delta?: any, zipCode?: string, bairro?: string
                      ) {
    const c = await lastValueFrom(this.http.post<Contact>(`${environment.backendUrl}/contacts`, {
      name, type, bairro: bairro ?? address, email, phone, birthday, gender, metadata, assigneeUserId, address, pictureId,
      cnpj, lineOfWork, addressNumber, addressComp, city, site, profession, delta, zipCode
    }));
    await this.refreshCurrentPage();
    return c;
  }
  async updateContact(id: string, name: string, type: string, address?: string, email?: string,
                      phone?: string, birthday?: number, gender?: string, metadata?: string,
                      assigneeUserId?: string, pictureId?: string, cnpj?: string, lineOfWork?: string,
                      addressNumber?: string, addressComp?: string, city?: string, site?: string, profession?: string, delta?: any, zipCode?: string, bairro?: string
  ) {
    const c = await lastValueFrom(this.http.put<Contact>(`${environment.backendUrl}/contacts/${id}`, {
      name, type, bairro: bairro ?? address, email, phone, birthday, gender, metadata, assigneeUserId, address, pictureId,
      cnpj, lineOfWork, addressNumber, addressComp, city, site, profession, delta, zipCode
    }));
    await this.refreshCurrentPage();
    return c;
  }

  async deleteContact(id: string) {
    await lastValueFrom(this.http.delete<{id: string}>(`${environment.backendUrl}/contacts/${id}`));
    await this.refreshCurrentPage();
  }

  cepLoading = signal(false);

  async fetchAddress(cep: string): Promise<Address | undefined> {
    let address;
    this.cepLoading.set(true);
    if (cep && cep.trim() !== '') {
      const data = await lastValueFrom(
        this.http.get<ViaCepResponse | null>(
          `https://viacep.com.br/ws/${cep}/json/`
        )
      ).catch(() => {
        // alert('Erro ao buscar o CEP.');
        return null;
      });
      if (data) {

        address = Address.fromViaCepResponse(data);
      } else {
        alert('Erro ao buscar o CEP.');
      }
      this.cepLoading.set(false);
    }
    return address;
  }
}
